import type { FC } from "react";
import { useMemo } from "react";

import {
    SearchInput,
    ChipDownContainer,
    EllipsisDropdownItem,
    Button,
    ColumnsPickerLite,
} from "@similarweb/ui-components";

import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { getExcelButtonText } from "app/components/React/ExcelButton/getExcelButtonText";
import { translate } from "app/filters/i18nFilter";
import { swSettings } from "app/common/services/swSettings";
import { DownloadExcelContainer } from "app/pages/workspace/StyledComponent";
import { SearchContainer } from "app/pages/workspace/StyledComponent";
import { allTrackers } from "app/services/track/track";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type {
    CategoryOption,
    ColumnsPickerProps,
    ColumnsPickerTranslations,
    ApiTrafficSourceFilter,
    FilterChangeEvent,
} from "../../types";

import { Right, CategoryItemContainer, SectionContainer } from "./styledComponent";

interface EnhancedSearchTableTopProps {
    onChange: (value: string) => void;
    onFilterChange: (filters: FilterChangeEvent, shouldReload?: boolean) => void;
    onClearAll: () => void;
    selectedCategory: string;
    selectedSourceType: string;
    searchTerm: string;
    allCategories: ApiTrafficSourceFilter[];
    allSourceTypes: ApiTrafficSourceFilter[];
    tableColumns: {
        key: string;
        displayName: string;
        visible: boolean;
    }[];
    onClickToggleColumns: (key: string) => void;
    excelLink?: string;
    totalCount?: number;
}

const CHIPDOWN_WIDTH = 340;

const getDisplayName = (text: string): string => {
    return text.replace(/~/g, " > ").replace(/_/g, " ");
};

const EnhancedSearchTableTop: FC<EnhancedSearchTableTopProps> = ({
    onChange,
    onFilterChange,
    onClearAll,
    selectedCategory,
    selectedSourceType,
    searchTerm,
    allCategories,
    allSourceTypes,
    tableColumns,
    onClickToggleColumns,
    excelLink,
    totalCount,
}) => {
    const convertCategory = (category: ApiTrafficSourceFilter): CategoryOption => ({
        text: `${category.text}${category.count ? ` (${category.count})` : ""}`,
        id: category.id,
        isCustomCategory: false,
        isChild: !category.children || category.children.length === 0,
        icon: category.icon || "",
        forApi: category.id,
    });

    const categoryOptions = allCategories
        .reduce<CategoryOption[]>((result, category) => {
            const converted = convertCategory(category);
            if (category.children && category.children.length > 0) {
                const children = category.children.map(convertCategory);
                return [...result, converted, ...children];
            }
            return [...result, converted];
        }, [])
        .map((item) => (
            <CategoryItemContainer
                {...item}
                key={item.id}
                selected={item.forApi === selectedCategory}
            />
        ));

    const isPaidExcelPermitted = isPaidExcelAllowed();

    const sourceOptions = allSourceTypes.map((source: ApiTrafficSourceFilter) => (
        <EllipsisDropdownItem
            id={source.id}
            key={source.id}
            text={source.text}
            infoText={source.count}
        >
            {source.text}
        </EllipsisDropdownItem>
    ));

    const handleSourceTypeSelect = (source: ApiTrafficSourceFilter | null): void => {
        const value = source?.id || "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Traffic Source/${value}`);

        onFilterChange(
            {
                sourceType: source?.id || "",
                search: searchTerm,
                category: selectedCategory || "",
            },
            false,
        );

        proNavigator.updateParams({ sourceType: source?.id });
    };

    const handleCategorySelect = (category: CategoryOption | null): void => {
        const value = category?.text || "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Referring Category/${value}`);

        onFilterChange(
            {
                sourceType: selectedSourceType,
                search: searchTerm,
                category: category?.forApi || "",
            },
            true,
        );

        proNavigator.updateParams({ tableCategory: category?.forApi || "no-category" });
    };

    const handleClearCategory = () => handleCategorySelect(null);
    const handleClearSourceType = () => handleSourceTypeSelect(null);

    const isClearAllDisabled = !(selectedCategory || selectedSourceType || searchTerm);
    const selectedCategoryText = selectedCategory ? getDisplayName(selectedCategory) : undefined;

    const translations: ColumnsPickerTranslations = useMemo(
        () => ({
            tooltip: translate("workspace.sales.column.picker"),
            getSelectedAmountText: ({ selectedValue, totalValue }) =>
                translate("common.columnsPicker.selectedAmount", { selected: selectedValue, total: totalValue }),
            mandatoryField: translate("common.columnsPicker.mandatoryField"),
            apply: translate("common.apply"),
        }),
        [],
    );

    const columnsPickerProps: ColumnsPickerProps = {
        columns: tableColumns.map(({ key, displayName, visible }) => ({ key, displayName, visible })),
        onColumnToggle: onClickToggleColumns,
        onPickerToggle: () => null,
        maxHeight: 264,
        width: "auto",
    };

    return (
        <div>
            <SectionContainer>
                <ChipDownContainer
                    width={CHIPDOWN_WIDTH}
                    onClick={handleCategorySelect}
                    selectedText={selectedCategoryText}
                    onCloseItem={handleClearCategory}
                    buttonText={translate("analysis.source.referrals.table.filter.categories")}
                    searchPlaceHolder={translate("analysis.source.referrals.table.filter.categories.searchCategory")}
                    noResultsMessage={translate("dropdown.no.results")}
                    hasSearch={true}
                >
                    {categoryOptions}
                </ChipDownContainer>
                <ChipDownContainer
                    width={CHIPDOWN_WIDTH}
                    onClick={handleSourceTypeSelect}
                    selectedText={selectedSourceType}
                    onCloseItem={handleClearSourceType}
                    buttonText={translate("analysis.source.referrals.table.filter.sources")}
                    searchPlaceHolder={translate("analysis.source.referrals.table.filter.sources.searchSource")}
                    noResultsMessage={translate("dropdown.no.results")}
                    tooltipDisabled={true}
                    hasSearch={true}
                >
                    {sourceOptions}
                </ChipDownContainer>
                <Button
                    type="flat"
                    isDisabled={isClearAllDisabled}
                    onClick={onClearAll}
                >
                    {translate("forms.buttons.clearall.text")}
                </Button>
            </SectionContainer>
            <SearchContainer>
                <SearchInput
                    clearValue={false}
                    debounce={400}
                    onChange={onChange}
                    placeholder={translate("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        {excelLink && (
                            <DownloadExcelContainer>
                                <DownloadButtonMenu
                                    iconNameProps={
                                        !swSettings.current.resources.IsExcelAllowed ? "excel-download" : null
                                    }
                                    Excel={true}
                                    downloadUrl={excelLink}
                                    excelLocked={!swSettings.current.resources.IsExcelAllowed}
                                    PaidExcel={isPaidExcelPermitted}
                                    totalCount={totalCount}
                                    buttonText={getExcelButtonText()}
                                    exportFunction={() => {
                                        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
                                    }}
                                />
                            </DownloadExcelContainer>
                        )}
                        <div>
                            <ColumnsPickerLite
                                {...columnsPickerProps}
                                withTooltip={true}
                                translations={translations}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </div>
    );
};

export default EnhancedSearchTableTop;
